import { type SharedData } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { 
    Smartphone, 
    Database, 
    Shield, 
    Zap, 
    Users, 
    Package, 
    ArrowRight, 
    CheckCircle, 
    Star,
    Play,
    Download,
    Globe,
    BarChart3,
    Settings
} from 'lucide-react';

export default function Welcome() {
    const { auth } = usePage<SharedData>().props;

    return (
        <>
            <Head title="Deep Corrugation Catalogue - Industrial Machinery Solutions">
                <link rel="preconnect" href="https://fonts.bunny.net" />
                <link href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600" rel="stylesheet" />
            </Head>
            
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
                {/* Navigation */}
                <nav className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-16">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                                        Deep Corrugation Machines
                                    </h1>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                {auth.user ? (
                                    <Link
                                        href={route('dashboard')}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Dashboard
                                    </Link>
                                ) : (
                                    <>
                                        <Link
                                            href={route('login')}
                                            className="text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                                        >
                                            Log in
                                        </Link>
                                        <Link
                                            href={route('register')}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            Get Started
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Hero Section */}
                <section className="relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                        <div className="text-center">
                            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
                                Industrial Machinery
                                <span className="text-blue-600 dark:text-blue-400"> Catalogue</span>
                            </h1>
                            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-3xl mx-auto">
                                Comprehensive digital catalog for corrugation machines, die cutting equipment, and industrial solutions. 
                                Browse, compare, and manage your machinery inventory with our advanced platform.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link
                                    href={route('register')}
                                    className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Start Free Trial
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Link>
                                <button className="inline-flex items-center px-8 py-3 border border-slate-300 dark:border-slate-600 text-base font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                    <Play className="mr-2 h-5 w-5" />
                                    Watch Demo
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-24 bg-white dark:bg-slate-900">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                                Powerful Features for Modern Industry
                            </h2>
                            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                                Everything you need to manage your industrial machinery catalog efficiently
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                                    <Database className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                    Comprehensive Database
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300">
                                    Store detailed specifications, images, and documentation for all your machinery in one centralized location.
                                </p>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mb-4">
                                    <Smartphone className="h-6 w-6 text-green-600 dark:text-green-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                    Mobile App Access
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300">
                                    Access your catalog anywhere with our mobile app. Browse products, view specifications, and manage inventory on the go.
                                </p>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4">
                                    <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                    Multi-User Management
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300">
                                    Collaborate with your team. Manage user permissions, track changes, and maintain data integrity across your organization.
                                </p>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center mb-4">
                                    <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                    Advanced Analytics
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300">
                                    Get insights into your catalog performance, popular products, and user engagement with detailed analytics and reports.
                                </p>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center mb-4">
                                    <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                    Enterprise Security
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300">
                                    Bank-level security with encrypted data, regular backups, and compliance with industry standards for data protection.
                                </p>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center mb-4">
                                    <Zap className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                    Lightning Fast
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300">
                                    Optimized performance with fast loading times, efficient search, and responsive design for the best user experience.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Mobile App Section */}
                <section className="py-24 bg-gradient-to-r from-blue-600 to-purple-600">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                    Mobile App Coming Soon
                                </h2>
                                <p className="text-xl text-blue-100 mb-8">
                                    Take your industrial catalog with you wherever you go. Our mobile app will provide seamless access to all your machinery data, specifications, and management tools.
                                </p>
                                <div className="space-y-4">
                                    <div className="flex items-center text-blue-100">
                                        <CheckCircle className="h-5 w-5 mr-3" />
                                        <span>Browse products on mobile devices</span>
                                    </div>
                                    <div className="flex items-center text-blue-100">
                                        <CheckCircle className="h-5 w-5 mr-3" />
                                        <span>View detailed specifications</span>
                                    </div>
                                    <div className="flex items-center text-blue-100">
                                        <CheckCircle className="h-5 w-5 mr-3" />
                                        <span>Offline access to catalog data</span>
                                    </div>
                                    <div className="flex items-center text-blue-100">
                                        <CheckCircle className="h-5 w-5 mr-3" />
                                        <span>QR code scanning for quick access</span>
                                    </div>
                                </div>
                                <div className="mt-8">
                                    <button className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white">
                                        <Download className="mr-2 h-5 w-5" />
                                        Get Early Access
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-center">
                                <div className="relative">
                                    <div className="w-64 h-96 bg-white rounded-3xl shadow-2xl p-4">
                                        <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center">
                                            <div className="text-center">
                                                <Smartphone className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                                                <p className="text-slate-600 font-medium">Mobile App</p>
                                                <p className="text-slate-500 text-sm">Coming Soon</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Product Categories */}
                <section className="py-24 bg-white dark:bg-slate-900">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                                Industrial Machinery Categories
                            </h2>
                            <p className="text-xl text-slate-600 dark:text-slate-300">
                                Comprehensive catalog covering all types of corrugation and industrial equipment
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <div className="group cursor-pointer">
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-8 rounded-lg border border-blue-200 dark:border-blue-700 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                                    <Package className="h-12 w-12 text-blue-600 dark:text-blue-400 mb-4" />
                                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                        Corrugation Machines
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300">
                                        High-speed corrugation machines, single facers, double backers, and complete production lines.
                                    </p>
                                </div>
                            </div>

                            <div className="group cursor-pointer">
                                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-8 rounded-lg border border-green-200 dark:border-green-700 group-hover:border-green-300 dark:group-hover:border-green-600 transition-colors">
                                    <Settings className="h-12 w-12 text-green-600 dark:text-green-400 mb-4" />
                                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                        Die Cutting Equipment
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300">
                                        Precision die cutting machines, creasing equipment, and automated cutting solutions.
                                    </p>
                                </div>
                            </div>

                            <div className="group cursor-pointer">
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-8 rounded-lg border border-purple-200 dark:border-purple-700 group-hover:border-purple-300 dark:group-hover:border-purple-600 transition-colors">
                                    <Globe className="h-12 w-12 text-purple-600 dark:text-purple-400 mb-4" />
                                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                                        Printing Solutions
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300">
                                        Flexographic printing machines, digital printing systems, and finishing equipment.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials */}
                <section className="py-24 bg-slate-50 dark:bg-slate-800">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                                Trusted by Industry Leaders
                            </h2>
                            <p className="text-xl text-slate-600 dark:text-slate-300">
                                See what our customers say about our platform
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-sm">
                                <div className="flex items-center mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                                    ))}
                                </div>
                                <p className="text-slate-600 dark:text-slate-300 mb-4">
                                    "This platform has revolutionized how we manage our machinery catalog. The mobile access is a game-changer for our field teams."
                                </p>
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 dark:text-blue-400 font-semibold">JD</span>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-slate-900 dark:text-white">John Doe</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Production Manager</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-sm">
                                <div className="flex items-center mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                                    ))}
                                </div>
                                <p className="text-slate-600 dark:text-slate-300 mb-4">
                                    "The comprehensive database and search functionality make it easy to find exactly what we need. Highly recommended!"
                                </p>
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                        <span className="text-green-600 dark:text-green-400 font-semibold">AS</span>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-slate-900 dark:text-white">Alice Smith</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Operations Director</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-sm">
                                <div className="flex items-center mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                                    ))}
                                </div>
                                <p className="text-slate-600 dark:text-slate-300 mb-4">
                                    "Excellent user interface and powerful features. The multi-user management has improved our team collaboration significantly."
                                </p>
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                                        <span className="text-purple-600 dark:text-purple-400 font-semibold">MJ</span>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-slate-900 dark:text-white">Mike Johnson</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Technical Lead</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-24 bg-slate-900 dark:bg-black">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Transform Your Machinery Catalog?
                        </h2>
                        <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                            Join hundreds of companies that have already modernized their industrial machinery management with our platform.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                href={route('register')}
                                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-slate-900 bg-white hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                            >
                                Start Free Trial
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Link>
                            <Link
                                href={route('login')}
                                className="inline-flex items-center px-8 py-3 border border-slate-600 text-base font-medium rounded-md text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                            >
                                Sign In
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="bg-slate-900 dark:bg-black border-t border-slate-800">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-4">CorrugationCatalogue</h3>
                                <p className="text-slate-400">
                                    Modern industrial machinery catalog management platform for the digital age.
                                </p>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="text-slate-400 hover:text-white">Features</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Mobile App</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Pricing</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">API</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="text-slate-400 hover:text-white">About</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Blog</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Careers</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Contact</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-white mb-4">Support</h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="text-slate-400 hover:text-white">Help Center</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Documentation</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Status</a></li>
                                    <li><a href="#" className="text-slate-400 hover:text-white">Security</a></li>
                                </ul>
                            </div>
                        </div>
                        <div className="border-t border-slate-800 mt-8 pt-8 text-center">
                            <p className="text-slate-400">
                                © 2025 Deep Corrugation Machines | All rights reserved | Developed & Maintained by <a href="https://www.thesquaredesigns.com" className="text-slate-400 hover:text-white">Square Designs</a>
                            </p>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
}
